import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import PresentationModeInfo, { PRESENTATION_MODE_TOOLTIP } from '../info-button/PresentationModeInfo';

const FEATURES = [
    { key: 'drawingEnabled', label: 'Drawing' },
    { key: 'imageQEnabled', label: 'Image Q' },
    { key: 'chatEnabled', label: 'Chat' },
    { key: 'reactionsEnabled', label: 'Reactions' }
];

/**
 * Admin-only Presentation Mode switch plus the per-feature settings that say
 * which features stay available to the audience while Presentation Mode is on.
 * The feature settings can be adjusted at any time and always persist; they
 * have no effect while Presentation Mode is off.
 *
 * @param settings - { drawingEnabled, imageQEnabled, chatEnabled, reactionsEnabled, presentationMode }
 * @param onChange - called with the full next settings object when an individual toggle flips
 * @param onTogglePresentationMode - called with the new boolean when Presentation Mode flips
 *        (the parent owns the flag so the same logic backs the prominent header control)
 */
export default function RoomSettingsDrawer({ settings = {}, onChange, onTogglePresentationMode }) {
    const setFeature = (key, value) => {
        onChange({ ...settings, [key]: value });
    };

    return (
        <Box>
            <Tooltip title={PRESENTATION_MODE_TOOLTIP}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={!!settings.presentationMode}
                            onChange={(e) => onTogglePresentationMode(e.target.checked)}
                            inputProps={{ 'aria-label': 'Presentation Mode' }}
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ fontWeight: 'bold' }}>Presentation Mode</Typography>
                            <PresentationModeInfo sx={{ ml: 0.5 }} />
                        </Box>
                    }
                />
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                While on, the audience only keeps the features switched on below.
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="overline" color="text.secondary">
                Available during Presentation Mode
            </Typography>
            <FormGroup>
                {FEATURES.map((f) => (
                    <FormControlLabel
                        key={f.key}
                        control={
                            <Switch
                                checked={!!settings[f.key]}
                                onChange={(e) => setFeature(f.key, e.target.checked)}
                            />
                        }
                        label={f.label}
                    />
                ))}
            </FormGroup>
        </Box>
    );
}
